import {
  cleanSourceText,
  findPageHeadlines,
  looksLikePageFurniture,
  splitSentences,
} from '../../briefs/clean-source-text.ts';
import { findResultTiles, isResultClaim } from '../../content-doctrine/result-evidence.ts';

/**
 * The sentences of a Brief's source page that its ad writer may cite: the same selection `extractClaims`
 * (`src/lib/briefs/extract-claims.ts`) makes, minus the ranking of results, so a figure the writer is
 * allowed to quote is a figure the claims check can read. A page that is not the brand's own has its
 * results tiles and result sentences left out, exactly as the writer leaves them out.
 *
 * `extractClaims` itself cannot load on the edge function (it imports by alias and without extensions),
 * so this is a port of its selection that imports only the two import-free helpers it is built from.
 * The length limits, the page-noise patterns and the prose test live here, and `extractClaims` imports
 * them, so the two cannot drift on those. `source-claims.test.ts` holds the rest of the selection
 * (what is dropped as a repeated headline or a results sentence) to the same sentences.
 */

export const MIN_CLAIM_CHARS = 24;
export const MAX_CLAIM_CHARS = 340;

/** Boilerplate that survives tag-stripping on almost every commerce page. */
export const CLAIM_NOISE = [
  /^(add to (cart|bag)|buy now|shop now|sign up|subscribe|log ?in|sign in)/i,
  /^(cookie|we use cookies|privacy policy|terms|all rights reserved|©)/i,
  /^if you (find|spot|see|notice) an? (error|mistake|typo)/i,
  /^(email|contact us|send (us )?a (tip|message))\b/i,
  /^(free shipping on orders|returns within|customer (service|care))/i,
  /^(skip to (main )?content|menu|search|cart|account)/i,
  /^\s*\d+\s*(reviews?|ratings?)\s*$/i,
];

/**
 * A claim asserts something; a product name or a nav label does not. The test is prose shape, not a verb
 * whitelist: enough words to be a sentence, and enough lowercase words to be prose rather than a run of
 * capitalised nouns. Over-inclusion is the safer error, since every claim is quoted verbatim.
 */
export function looksLikeClaim(sentence: string): boolean {
  const words = sentence.split(/\s+/).filter(Boolean);
  if (words.length < 5) return false;
  return words.filter((word) => /^[a-z]/.test(word)).length >= 2;
}

export function normalizeForCompare(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function sourceClaimSentences(
  text: string,
  options: { brandOwned?: boolean; limit?: number } = {},
): string[] {
  if (!text) return [];
  const limit = options.limit ?? 60;
  const brandOwned = options.brandOwned === true;
  const cleaned = cleanSourceText(text);
  const normalizedWhole = normalizeForCompare(cleaned);
  const normalizedHeadlines = findPageHeadlines(text)
    .map(normalizeForCompare)
    .filter((headline) => headline.length >= 20);
  const seen = new Set<string>();
  const claims: string[] = [];

  if (brandOwned) {
    for (const tile of findResultTiles(cleaned)) {
      const normalized = normalizeForCompare(tile);
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      claims.push(tile);
    }
    for (const raw of splitSentences(cleaned)) {
      const sentence = raw.replace(/\s+/g, ' ').trim();
      if (sentence.length < MIN_CLAIM_CHARS || sentence.length > MAX_CLAIM_CHARS) continue;
      if (CLAIM_NOISE.some((re) => re.test(sentence)) || looksLikePageFurniture(sentence)) continue;
      if (!looksLikeClaim(sentence) || !isResultClaim(sentence)) continue;
      const normalized = normalizeForCompare(sentence);
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      claims.push(sentence);
    }
  }

  for (const raw of splitSentences(cleaned)) {
    const sentence = raw.replace(/\s+/g, ' ').trim();
    if (sentence.length < MIN_CLAIM_CHARS || sentence.length > MAX_CLAIM_CHARS) continue;
    if (CLAIM_NOISE.some((re) => re.test(sentence)) || looksLikePageFurniture(sentence)) continue;
    if (!looksLikeClaim(sentence)) continue;
    if (!brandOwned && isResultClaim(sentence)) continue;
    const normalized = normalizeForCompare(sentence);
    if (normalizedHeadlines.some((headline) => normalized.includes(headline))) continue;
    const opening = normalized.slice(0, 60);
    if (
      opening.length >= 40 &&
      normalizedWhole.split(opening).length - 1 >= 2 &&
      normalizedWhole.split(normalized).length - 1 <= 1
    ) {
      continue;
    }
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    claims.push(sentence);
    if (claims.length >= limit) break;
  }
  return claims.slice(0, limit);
}
